import type { WorkspaceVersionGroup } from '@stratumn/studio-shared';
import { CheckIcon, GitBranch } from 'lucide-react';
import { useState } from 'react';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FilterPill } from './FilterPill';
import { type FilterOperator, isMultiOperator } from './types';

const VERSION_GROUP_OPTIONS: {
  value: WorkspaceVersionGroup;
  label: string;
}[] = [
  { value: 'stable', label: 'Stable' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'dev', label: 'Dev' }
];

type VersionGroupFilterPickerProps = {
  selectedGroups: string[];
  operator: FilterOperator;
  onChange: (groups: string[], operator: FilterOperator) => void;
  onRemove: () => void;
  /** When true, the value popover opens automatically on mount */
  autoOpen?: boolean;
};

export const VersionGroupFilterPicker = ({
  selectedGroups,
  operator,
  onChange,
  onRemove,
  autoOpen = false
}: VersionGroupFilterPickerProps) => {
  const [open, setOpen] = useState(autoOpen);
  const isMulti = isMultiOperator(operator);

  const handleSelect = (group: string) => {
    if (!isMulti) {
      onChange([group], operator);
      setOpen(false);
      return;
    }

    if (selectedGroups.includes(group)) {
      onChange(
        selectedGroups.filter(g => g !== group),
        operator
      );
    } else {
      onChange([...selectedGroups, group], operator);
    }
  };

  const handleOperatorChange = (newOp: FilterOperator) => {
    if (!isMultiOperator(newOp) && selectedGroups.length > 1) {
      onChange([selectedGroups.at(-1)], newOp);
    } else {
      onChange(selectedGroups, newOp);
    }
  };

  const renderValueSummary = () => {
    if (selectedGroups.length === 0) {
      return <span className="text-muted-foreground">Select...</span>;
    }

    if (selectedGroups.length === 1) {
      const option = VERSION_GROUP_OPTIONS.find(
        o => o.value === selectedGroups[0]
      );
      if (option) {
        return <span>{option.label}</span>;
      }
    }

    return <span>{selectedGroups.length} groups</span>;
  };

  return (
    <FilterPill
      icon={<GitBranch className="size-3.5" />}
      label="Version"
      onOperatorChange={handleOperatorChange}
      onRemove={onRemove}
      operator={operator}
    >
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <button
            className="flex h-full items-center gap-1.5 px-2 hover:bg-accent"
            type="button"
          >
            {renderValueSummary()}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-48 p-0">
          <Command className="border-none shadow-none">
            <CommandList>
              <CommandGroup>
                {VERSION_GROUP_OPTIONS.map(option => {
                  const isSelected = selectedGroups.includes(option.value);
                  return (
                    <CommandItem
                      className="cursor-pointer"
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                    >
                      {isMulti && (
                        <div
                          className={cn(
                            'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : '[&_svg]:invisible'
                          )}
                        >
                          <CheckIcon className="size-3" />
                        </div>
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FilterPill>
  );
};
